import CheckGray from "@/public/assets/icons/check-gray.svg"
import StearsDashboard from "@/public/assets/img/stears-dashboard.svg"
import useTheme from "@/src/common/hooks/useTheme"
import classNames from "classnames"
import Image from "next/image"
import React, { ReactNode } from "react"

import { pages } from "../../constants/copy"
import Logo from "../Logo"
import { SimpleLink } from "../SimpleLink"

type AuthLayoutProps = {
  children: ReactNode
  headerLink?: {
    text: string
    href: string
  }
  hideHeader?: boolean
  hideHeaderLinks?: boolean
  contentClassName?: string
}

const DEFAULT_CONTENT_CLASSES =
  "portrait_tablet:max-w-[480px] portrait_tablet:px-4 pt-[160px] portrait_tablet:pt-[100px] tablet:pt-0"

const features = [
  "Discover companies, track deals, and assess investment risks",
  "Evaluate sectors, market trends, and growth opportunities",
  "Benchmark fund managers, LPs, and capital flows",
  "Access macroeconomic and regulatory insights, built by local experts"
]

export const AuthLayout = (props: AuthLayoutProps) => {
  const { children, hideHeader, contentClassName } = props
  const { theme } = useTheme()
  return (
    <div className={theme}>
      <div className="overflow-y-auto bg-gray-100 tablet:h-auto portrait_tablet:bg-stears_white tablet:bg-stears_white portrait_tablet:h-auto dark:bg-[#0E0F10]">
        <div className="flex">
          <div className="portrait_tablet:hidden w-full max-w-[614px] desktop:max-w-[522px] min-h-screen dark:bg-stears_black dark:border-r-[0.2px] dark:border-[#4b556383]">
            <div className="tablet:hidden portrait_tablet:hidden pt-10 pl-4 mb-[122px] portrait_tablet:bg-stears_white tablet:bg-stears_white">
              <SimpleLink href={pages.landingPage} color="black">
                <Logo type={theme === "dark" ? "white" : "black"} />
              </SimpleLink>
            </div>
            <Image
              src={StearsDashboard}
              alt="Stears Dashboard Image"
              width={608}
              height={377}
              className=""
              priority
            />
            <div className="pl-10 text-xs dark:text-white">
              <h1 className="text-xl font-bold pr-20">
                Africa-first financial intelligence
              </h1>
              <p className="mb-5 mt-1.5 tracking-tighter text-[12px] max-w-[442px]">
                Built from the ground up for fund managers, advisors and
                corporates deploying capital across African markets.
              </p>
              <div className="space-y-2.5">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 tracking-tighter"
                  >
                    <Image
                      src={CheckGray}
                      alt="Check icon"
                      width={16}
                      height={16}
                      className="h-4 w-4"
                      priority
                    />
                    <p>{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={classNames(
              "flex-1 h-auto portrait_tablet:h-full tablet:text-center portrait_tablet:text-center py-2",
              [hideHeader ? "min-h-[100vh]" : "min-h-[calc(100vh-60px)]"]
            )}
          >
            <div className="hidden portrait_tablet:flex items-center w-full border-b-[0.5px] border-[#4B5563] dark:bg-black h-[60px]">
              <SimpleLink href={pages.landingPage} color="black">
                <Logo type={"white"} />
              </SimpleLink>
            </div>
            <div className="flex justify-center items-center w-full">
              <div
                className={classNames(
                  "xl:min-w-[480px] portrait_tablet:w-full bg-stears_white rounded-2xl h-fit pb-14 dark:bg-[#0E0F10]",
                  contentClassName ?? DEFAULT_CONTENT_CLASSES
                )}
              >
                <div className="p-10 portrait_tablet:px-0">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
